import React, {useEffect, useState} from 'react';
import {App, Pagination, Spin} from 'antd';
import {CheckOutlined, CopyOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import api from '../../services/api';
import './StrategySquarePage.css';

interface MarketStrategy {
    id: number;
    title: string;
    description: string;
    category: string;
    strategyCode: string;
    profit: number;
    winRate: number;
    maxDrawdown: number;
    userCount: number;
    authorName: string;
    authorAvatar: string;
    rating: number;
    chartData: string;
    isFeatured: number;
    isCopied?: boolean;
}

interface PageData {
    content: MarketStrategy[];
    totalElements: number;
    totalPages: number;
    currentPage: number;
    size: number;
}

const StrategySquarePage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [featuredLoading, setFeaturedLoading] = useState(false);
    const [category, setCategory] = useState('all');
    const [pageData, setPageData] = useState<PageData>({
        content: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
        size: 12
    });
    const [featuredStrategies, setFeaturedStrategies] = useState<MarketStrategy[]>([]);
    const [selectedStrategy, setSelectedStrategy] = useState<MarketStrategy | null>(null);

    useEffect(() => {
        loadFeaturedStrategies();
    }, []);

    useEffect(() => {
        loadStrategies(0);
    }, [category]);

    const loadFeaturedStrategies = async () => {
        setFeaturedLoading(true);
        try {
            const response = await api.get('/api/v1/market-strategies/featured');
            if (response.data.success && response.data.data) {
                setFeaturedStrategies(response.data.data);
            }
        } catch (error) {
            console.error('加载精选策略失败', error);
        } finally {
            setFeaturedLoading(false);
        }
    };

    const loadStrategies = async (page: number) => {
        setLoading(true);
        try {
            const response = await api.get('/api/v1/market-strategies', {
                params: {category, page, size: 12}
            });
            if (response.data.success && response.data.data) {
                setPageData({
                    content: response.data.data.content || [],
                    totalElements: response.data.data.totalElements || 0,
                    totalPages: response.data.data.totalPages || 0,
                    currentPage: response.data.data.currentPage || 0,
                    size: response.data.data.size || 12
                });
            }
        } catch (error) {
            console.error('加载策略列表失败', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newCategory: string) => {
        setCategory(newCategory);
    };

    const handlePageChange = (page: number) => {
        loadStrategies(page - 1);
    };

    const handleCopyStrategy = async (id: number) => {
        try {
            // 检查是否已经复制过
            const isAlreadyCopied = pageData?.content.some(s => s.id === id && s.isCopied)
                || featuredStrategies.some(s => s.id === id && s.isCopied);
            if (isAlreadyCopied) {
                messageApi.warning('该策略已复制过');
                navigate('/strategy/list');
                return;
            }

            const response = await api.post(`/api/v1/market-strategies/${id}/copy`);
            if (response.data.success) {
                messageApi.success(`策略复制成功！已添加到您的策略列表`);
                // 更新策略列表中对应策略的isCopied状态
                setPageData(prev => ({
                    ...prev,
                    content: prev.content.map(s => s.id === id ? {...s, isCopied: true} : s)
                }));
                // 更新精选策略中的状态
                setFeaturedStrategies(prev => prev.map(s => s.id === id ? {...s, isCopied: true} : s));
            } else {
                messageApi.error(response.data.message || '复制失败');
            }
        } catch (error: any) {
            console.error('复制策略失败', error);
            messageApi.error('复制失败，请重试');
        }
    };

    const handleBack = () => {
        setSelectedStrategy(null);
    };

    const handleStartBacktest = async () => {
        if (!selectedStrategy) return;

        // 检查是否已经复制过
        const isAlreadyCopied = pageData?.content.some(s => s.id === selectedStrategy.id && s.isCopied)
            || featuredStrategies.some(s => s.id === selectedStrategy.id && s.isCopied);
        if (isAlreadyCopied) {
            messageApi.warning('该策略已复制过');
            navigate('/strategy/list');
            return;
        }

        // 先复制策略到我的账户
        try {
            await handleCopyStrategy(selectedStrategy.id);
            // 复制成功后跳转到回测页面
            navigate('/strategy/backtest');
        } catch (error) {
            messageApi.error('操作失败，请重试');
        }
    };

    const getCategoryTag = (cat: string) => {
        const categoryMap: Record<string, { bg: string; color: string; label: string }> = {
            trend: {bg: 'var(--pt-bull-dim)', color: 'var(--pt-bull)', label: '趋势跟踪'},
            mean: {bg: 'var(--pt-purple-dim)', color: 'var(--pt-purple)', label: '均值回归'},
            grid: {bg: 'var(--pt-blue-dim)', color: 'var(--pt-blue)', label: '网格策略'}
        };
        const config = categoryMap[cat] || {bg: 'var(--pt-primary-dim)', color: 'var(--pt-primary)', label: cat};
        return (
            <span style={{
                fontSize: '0.72rem',
                padding: '3px 10px',
                borderRadius: '20px',
                background: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`
            }}>
        {config.label}
      </span>
        );
    };

    const getCategoryTagForFeatured = (cat: string) => {
        const categoryMap: Record<string, { bg: string; color: string; label: string }> = {
            trend: {bg: 'var(--pt-bull-dim)', color: 'var(--pt-bull)', label: '趋势跟踪'},
            mean: {bg: 'var(--pt-purple-dim)', color: 'var(--pt-purple)', label: '均值回归'},
            grid: {bg: 'var(--pt-blue-dim)', color: 'var(--pt-blue)', label: '网格策略'}
        };
        const config = categoryMap[cat] || {bg: 'var(--pt-primary-dim)', color: 'var(--pt-primary)', label: cat};
        return (
            <span style={{
                fontSize: '0.72rem',
                padding: '3px 10px',
                borderRadius: '20px',
                background: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`
            }}>
        {config.label}
      </span>
        );
    };

    const parseChartData = (chartData: string): string => {
        if (!chartData) return '';
        try {
            const data = JSON.parse(chartData);
            return data.map((p: { x: number; y: number }) => `${p.x},${p.y}`).join(' ');
        } catch {
            return '';
        }
    };

    const getChartColor = (cat: string) => {
        const colorMap: Record<string, string> = {
            trend: 'var(--pt-bull)',
            mean: 'var(--pt-purple)',
            grid: 'var(--pt-blue)'
        };
        return colorMap[cat] || 'var(--pt-primary)';
    };

    const renderStrategyDetail = () => {
        if (!selectedStrategy) return null;

        const strategy = selectedStrategy;
        const chartColor = getChartColor(strategy.category);
        const points = parseChartData(strategy.chartData);
        const chartPoints = points || '0,30 40,28 80,25 120,22 160,18 200,15 240,12 280,10 320,8 360,6 400,5 440,4 480,3 520,2 560,1';

        return (
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                <div className="dash-header">
                    <div>
                        <h2>策略详解</h2>
                        <p>先理解策略逻辑，再进行回测</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-sm btn-sm-ghost" onClick={handleBack}>← 返回列表</button>
                        <button className="btn-sm btn-sm-gold" onClick={handleStartBacktest}>开始回测 →</button>
                    </div>
                </div>

                <div className="explain-wrap">
                    <div className="explain-main">
                        <div className="explain-h">{strategy.title}</div>
                        <div className="explain-sub">在开始回测之前，花 3 分钟先真正看懂这个策略在做什么</div>

                        <div className="ai-insight" style={{marginBottom: '2rem'}}>
                            {strategy.description}
                        </div>

                        <div className="chart-area">
                            <div className="chart-legend">
                                <div className="legend-item">
                                    <div className="legend-dot" style={{background: 'var(--pt-text-secondary)'}}></div>
                                    BTC 价格
                                </div>
                                <div className="legend-item">
                                    <div className="legend-dot" style={{background: 'var(--pt-primary)'}}></div>
                                    5日均线（短期）
                                </div>
                                <div className="legend-item">
                                    <div className="legend-dot" style={{background: 'var(--pt-purple)'}}></div>
                                    20日均线（长期）
                                </div>
                                <div className="legend-item">
                                    <div style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '2px',
                                        background: 'rgba(0, 217, 126, 0.3)',
                                        border: '1px solid var(--pt-bull)'
                                    }}></div>
                                    买入信号
                                </div>
                            </div>
                            <div className="chart-svg-wrap">
                                <svg width="100%" height="100%" viewBox="0 0 600 200"
                                     preserveAspectRatio="xMidYMid meet">
                                    <polyline
                                        points="0,160 40,150 80,140 120,120 160,100 200,90 220,100 240,115 260,105 300,80 340,60 380,50 420,55 460,45 500,35 540,30 580,25"
                                        fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"></polyline>
                                    <polyline
                                        points="0,162 40,158 80,152 120,142 160,128 200,115 240,110 280,102 320,90 360,75 400,62 440,54 480,48 520,42 560,36"
                                        fill="none" stroke="var(--pt-purple)" strokeWidth="1.5"
                                        strokeOpacity="0.7"></polyline>
                                    <polyline
                                        points="0,158 30,148 60,135 90,118 130,98 170,88 210,95 240,108 270,95 310,72 350,54 390,48 430,50 460,43 500,33 540,28 580,22"
                                        fill="none" stroke="var(--pt-primary)" strokeWidth="2"></polyline>
                                    <circle cx="130" cy="98" r="5" fill="var(--pt-bull)" opacity="0.9"></circle>
                                    <line x1="130" y1="85" x2="130" y2="180" stroke="var(--pt-bull)" strokeWidth="1"
                                          strokeDasharray="3,3" opacity="0.4"></line>
                                    <text x="134" y="82" fill="var(--pt-bull)" fontSize="9"
                                          fontFamily="IBM Plex Mono">BUY
                                    </text>
                                    <circle cx="340" cy="54" r="5" fill="var(--pt-bull)" opacity="0.9"></circle>
                                    <line x1="340" y1="41" x2="340" y2="180" stroke="var(--pt-bull)" strokeWidth="1"
                                          strokeDasharray="3,3" opacity="0.4"></line>
                                    <text x="344" y="38" fill="var(--pt-bull)" fontSize="9"
                                          fontFamily="IBM Plex Mono">BUY
                                    </text>
                                    <circle cx="222" cy="100" r="5" fill="var(--pt-bear)" opacity="0.9"></circle>
                                    <line x1="222" y1="87" x2="222" y2="180" stroke="var(--pt-bear)" strokeWidth="1"
                                          strokeDasharray="3,3" opacity="0.4"></line>
                                    <text x="226" y="84" fill="var(--pt-bear)" fontSize="9"
                                          fontFamily="IBM Plex Mono">SELL
                                    </text>
                                    <line x1="0" y1="185" x2="600" y2="185" stroke="var(--pt-border-subtle)"
                                          strokeWidth="1"></line>
                                    <text x="0" y="198" fill="var(--pt-text-dim)" fontSize="9"
                                          fontFamily="IBM Plex Mono">1月
                                    </text>
                                    <text x="140" y="198" fill="var(--pt-text-dim)" fontSize="9"
                                          fontFamily="IBM Plex Mono">4月
                                    </text>
                                    <text x="290" y="198" fill="var(--pt-text-dim)" fontSize="9"
                                          fontFamily="IBM Plex Mono">7月
                                    </text>
                                    <text x="440" y="198" fill="var(--pt-text-dim)" fontSize="9"
                                          fontFamily="IBM Plex Mono">10月
                                    </text>
                                </svg>
                            </div>
                        </div>

                        <div className="param-section">
                            <h4>调整策略参数（拖动滑块，实时感受变化）</h4>
                            <div className="param-row">
                                <div className="param-lbl">短期均线（天）</div>
                                <input type="range" className="param-slider" min="3" max="20" defaultValue="5" id="sl1"
                                       onInput={(e) => {
                                           const target = e.target as HTMLInputElement;
                                           const el = document.getElementById('sv1');
                                           if (el) el.textContent = target.value;
                                       }}></input>
                                <div className="param-val" id="sv1">5</div>
                            </div>
                            <div className="param-row">
                                <div className="param-lbl">长期均线（天）</div>
                                <input type="range" className="param-slider" min="10" max="60" defaultValue="20"
                                       id="sl2" onInput={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    const el = document.getElementById('sv2');
                                    if (el) el.textContent = target.value;
                                }}></input>
                                <div className="param-val" id="sv2">20</div>
                            </div>
                            <div className="param-row">
                                <div className="param-lbl">止损比例</div>
                                <input type="range" className="param-slider" min="5" max="30" defaultValue="12" id="sl3"
                                       onInput={(e) => {
                                           const target = e.target as HTMLInputElement;
                                           const el = document.getElementById('sv3');
                                           if (el) el.textContent = target.value + '%';
                                       }}></input>
                                <div className="param-val" id="sv3">12%</div>
                            </div>
                        </div>
                    </div>

                    <div className="explain-sidebar">
                        <div className="explain-section">
                            <h4>这个策略在做什么</h4>
                            <div className="insight-card">
                                <h5>核心逻辑</h5>
                                <p style={{
                                    fontSize: '0.875rem',
                                    lineHeight: 1.65
                                }}>当价格短期动能超过长期趋势时买入，短期动能低于长期趋势时卖出。跟随趋势，不猜顶和底。</p>
                            </div>
                        </div>

                        <div className="explain-section">
                            <h4>为什么这样做有效</h4>
                            <div className="ai-insight" style={{fontSize: '0.82rem'}}>
                                加密市场存在明显的<em>动量效应</em>——上涨的资产往往会继续上涨一段时间。均线策略捕捉的正是这个效应，代价是信号有一定延迟，不会抄到最低点。
                            </div>
                        </div>

                        <div className="explain-section">
                            <h4>它在什么时候会失效</h4>
                            <div className="warn-box">
                                <span>⚠️</span>
                                <span>在剧烈震荡的横盘市场中，均线会频繁交叉产生假信号，导致连续小亏损（俗称「被锯」）。</span>
                            </div>
                        </div>

                        <div className="explain-section">
                            <h4>关键问题</h4>
                            <div className="insight-card" style={{marginBottom: '.75rem'}}>
                                <h5>如果我在最差时刻入金</h5>
                                <p style={{
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    color: 'var(--pt-bear)'
                                }}>2022年熊市，10万本金最低跌至约 8.2万 USDT，浮亏 18%。这段时间需要你坚持持有。</p>
                            </div>
                            <div className="insight-card">
                                <h5>信号多久触发一次</h5>
                                <p style={{fontSize: '0.875rem', lineHeight: 1.6}}>平均每月 2–4
                                    次交易信号，不是高频策略，适合不想天天盯盘的新手。</p>
                            </div>
                        </div>

                        <button className="btn-primary" style={{width: '100%', marginTop: 'auto'}}
                                onClick={handleStartBacktest}>我看懂了，开始回测 →
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderFeaturedStrategy = () => {
        if (featuredStrategies.length === 0) return null;
        const strategy = featuredStrategies[0];

        return (
            <div style={{
                background: 'linear-gradient(135deg, var(--pt-bg-panel), rgba(240,180,41,0.06))',
                border: '1px solid rgba(240,180,41,0.2)',
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '2rem',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '2rem',
                alignItems: 'center'
            }}>
                <div>
                    <div style={{
                        fontSize: '.72rem',
                        color: 'var(--pt-primary)',
                        fontWeight: 600,
                        letterSpacing: '.06em',
                        marginBottom: '.4rem'
                    }}>
                        本周精选 · 编辑推荐
                    </div>
                    <h3 style={{fontSize: '1.2rem', fontWeight: 700, marginBottom: '.4rem'}}>{strategy.title}</h3>
                    <p style={{
                        fontSize: '.875rem',
                        color: 'var(--pt-text-secondary)',
                        lineHeight: 1.65,
                        maxWidth: 480,
                        marginBottom: '1rem'
                    }}>
                        {strategy.description}
                    </p>
                    <div style={{display: 'flex', gap: '1.5rem', fontSize: '.82rem'}}>
                        <span
                            style={{color: 'var(--pt-bull)'}}>近1年 {strategy.profit ? (strategy.profit * 100).toFixed(1) : 0}%</span>
                        <span
                            style={{color: 'var(--pt-text-secondary)'}}>胜率 {strategy.winRate || 0}% · 最大回撤 {strategy.maxDrawdown ? (strategy.maxDrawdown * 100).toFixed(0) : 0}%</span>
                        <span
                            style={{color: 'var(--pt-text-secondary)'}}>使用人数 {strategy.userCount?.toLocaleString() || 0}</span>
                    </div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '.75rem', alignItems: 'flex-end'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: 'var(--pt-bull-dim)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '.65rem', fontWeight: 700, color: 'var(--pt-bull)',
                            fontFamily: "'Syne', sans-serif"
                        }}>
                            {strategy.authorName?.charAt(0) || 'A'}
                        </div>
                        <span style={{fontSize: '.82rem', color: 'var(--pt-text-secondary)'}}>
              by {strategy.authorName} · ★ {strategy.rating || 0}
            </span>
                    </div>
                    <button
                        onClick={() => handleCopyStrategy(strategy.id)}
                        style={{
                            background: 'var(--pt-primary)', color: '#000', border: 'none',
                            padding: '.65rem 1.5rem', borderRadius: 8,
                            fontSize: '.875rem', fontWeight: 700, cursor: 'pointer',
                            fontFamily: "'Syne', sans-serif", whiteSpace: 'nowrap'
                        }}
                    >
                        复制此策略 →
                    </button>
                </div>
            </div>
        );
    };

    const renderStrategyCard = (strategy: MarketStrategy) => {
        const chartColor = getChartColor(strategy.category);
        const points = parseChartData(strategy.chartData);

        return (
            <div
                key={strategy.id}
                className="market-card"
                onClick={() => setSelectedStrategy(strategy)}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '.75rem'
                }}>
                    {getCategoryTag(strategy.category)}
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        {strategy.isCopied ? (
                            <CheckOutlined style={{color: 'var(--pt-bull)', fontSize: '1rem'}}/>
                        ) : (
                            <CopyOutlined
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyStrategy(strategy.id);
                                }}
                                style={{color: 'var(--pt-primary)', fontSize: '1rem', cursor: 'pointer'}}
                            />
                        )}
                        <div style={{
                            fontSize: '.75rem',
                            color: 'var(--pt-text-dim)'
                        }}>使用 {strategy.userCount?.toLocaleString() || 0} 人
                        </div>
                    </div>
                </div>
                <div style={{fontSize: '.975rem', fontWeight: 700, marginBottom: '.3rem'}}>{strategy.title}</div>
                <div style={{
                    fontSize: '.8rem',
                    color: 'var(--pt-text-secondary)',
                    lineHeight: 1.6,
                    marginBottom: '1rem'
                }}>
                    {strategy.description}
                </div>
                <svg width="100%" height="40" viewBox="0 0 160 40" preserveAspectRatio="none"
                     style={{marginBottom: '.875rem'}}>
                    <polyline points={points} fill="none" stroke={chartColor} strokeWidth="1.5"/>
                </svg>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '.8rem'}}>
                    <span className="pos">{strategy.profit ? (strategy.profit * 100).toFixed(1) : 0}%</span>
                    <span style={{color: 'var(--pt-text-secondary)'}}>胜率 {strategy.winRate || 0}%</span>
                    <span className="neg">{strategy.maxDrawdown ? (strategy.maxDrawdown * 100).toFixed(0) : 0}%</span>
                </div>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginTop: '.875rem', paddingTop: '.875rem', borderTop: '1px solid var(--pt-border-subtle)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: '.78rem',
                        color: 'var(--pt-text-secondary)'
                    }}>
                        <div style={{
                            width: 20, height: 20, borderRadius: '50%',
                            background: 'var(--pt-purple-dim)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '.6rem', fontWeight: 700, color: 'var(--pt-purple)',
                            fontFamily: "'Syne', sans-serif"
                        }}>
                            {strategy.authorName?.charAt(0) || 'A'}
                        </div>
                        <span>{strategy.authorName || '匿名'}</span>
                    </div>
                    <span style={{color: 'var(--pt-primary)', fontSize: '.8rem'}}>★ {strategy.rating || 0}</span>
                </div>
            </div>
        );
    };

    return (
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto'}}>
            {selectedStrategy ? (
                renderStrategyDetail()
            ) : (
                <>
                    <div className="dash-header">
                        <div>
                            <h2>策略广场</h2>
                            {/*<p>浏览社区共享策略，一键复制到你的账户</p>*/}
                        </div>
                        <div className="header-actions">
                            <div style={{
                                display: 'flex', gap: 6, background: 'var(--pt-bg-card)',
                                border: '1px solid var(--pt-border)', borderRadius: 8, padding: 4
                            }}>
                                <button
                                    id="filter-all"
                                    onClick={() => handleFilterChange('all')}
                                    style={{
                                        fontSize: '0.78rem', padding: '0.35rem 0.875rem', borderRadius: 6,
                                        background: category === 'all' ? 'var(--pt-text-primary)' : 'none',
                                        color: category === 'all' ? 'var(--pt-bg-base)' : 'var(--pt-text-secondary)',
                                        border: 'none', cursor: 'pointer',
                                        fontFamily: "'Syne', sans-serif", fontWeight: 600
                                    }}
                                >
                                    全部
                                </button>
                                <button
                                    id="filter-trend"
                                    onClick={() => handleFilterChange('trend')}
                                    style={{
                                        fontSize: '0.78rem', padding: '0.35rem 0.875rem', borderRadius: 6,
                                        background: category === 'trend' ? 'var(--pt-text-primary)' : 'none',
                                        color: category === 'trend' ? 'var(--pt-bg-base)' : 'var(--pt-text-secondary)',
                                        border: 'none', cursor: 'pointer', fontWeight: 400
                                    }}
                                >
                                    趋势跟踪
                                </button>
                                <button
                                    id="filter-mean"
                                    onClick={() => handleFilterChange('mean')}
                                    style={{
                                        fontSize: '0.78rem', padding: '0.35rem 0.875rem', borderRadius: 6,
                                        background: category === 'mean' ? 'var(--pt-text-primary)' : 'none',
                                        color: category === 'mean' ? 'var(--pt-bg-base)' : 'var(--pt-text-secondary)',
                                        border: 'none', cursor: 'pointer', fontWeight: 400
                                    }}
                                >
                                    均值回归
                                </button>
                                <button
                                    id="filter-grid"
                                    onClick={() => handleFilterChange('grid')}
                                    style={{
                                        fontSize: '0.78rem', padding: '0.35rem 0.875rem', borderRadius: 6,
                                        background: category === 'grid' ? 'var(--pt-text-primary)' : 'none',
                                        color: category === 'grid' ? 'var(--pt-bg-base)' : 'var(--pt-text-secondary)',
                                        border: 'none', cursor: 'pointer', fontWeight: 400
                                    }}
                                >
                                    网格策略
                                </button>
                            </div>
                            {/*<button className="btn-sm btn-sm-ghost" onClick={() => navigate('/page-builder')}>*/}
                            {/*  发布我的策略*/}
                            {/*</button>*/}
                        </div>
                    </div>

                    <div style={{padding: '2rem'}}>
                        {featuredLoading ? (
                            <div style={{textAlign: 'center', padding: '2rem'}}>
                                <Spin size="large"/>
                            </div>
                        ) : (
                            renderFeaturedStrategy()
                        )}

                        {loading ? (
                            <div style={{textAlign: 'center', padding: '2rem'}}>
                                <Spin size="large"/>
                            </div>
                        ) : (
                            <>
                                <div className="market-grid">
                                    {pageData.content.map(strategy => renderStrategyCard(strategy))}
                                </div>
                                {pageData.totalElements > 0 && (
                                    <div style={{display: 'flex', justifyContent: 'center', marginTop: '2rem'}}>
                                        <Pagination
                                            current={pageData.currentPage + 1}
                                            pageSize={pageData.size}
                                            total={pageData.totalElements}
                                            onChange={handlePageChange}
                                            showSizeChanger={false}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default StrategySquarePage;
